import React, { useEffect, useState, useCallback, useMemo } from "react";
import { t } from "../../../../utils/translate";
import {
  createAfterControlFormService,
  getAfterControlFormService,
} from "../../../../Services/afterControlFormService";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import _ from "lodash";
import MainLayout from "../../../components/layout/MainLayout";
import SignatureModal from "../../../../components/SignatureModal";
import ModalVisual from "../../../../components/forms/ModelVisual";
import Modal from "../../../../components/forms/Modal";
import { getProjectByUserId } from "../../../../Services/projectService";

const AfterControl = () => {
  // Router and Redux hooks
  const navigate = useNavigate();
  const { loggedInUser } = useSelector((state) => state.auth);
  const userId = loggedInUser?._id;
  const approvalName = useSelector((state) => state?.auth?.loggedInUser?.name);
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [isProjectOpen, setIsProjectOpen] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const filteredProjects = projects.filter((project) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = project.projectName.toLowerCase().includes(searchLower);
    const numberMatch = project.projectNumber.toString().includes(searchLower);
    return nameMatch || numberMatch;
  });

  // Data states
  const [afterControl, setAfterControl] = useState([]);
  const [controlName, setControlName] = useState("");

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  // Modal states
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalOpenone, setModalOpenone] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalVisual, setShowModalVisual] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [modalVisual, setModalVisual] = useState("");
  const [recentViewVisual, setRecentViewVisual] = useState([]);

  // Signature states
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureImageone, setSignatureImageone] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [approverName, setApproverName] = useState("");

  // Format date string for control name
  const formatDateString = useCallback(() => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}${month}${year}${hours}${minutes}`;
  }, []);

  // Fetch data only when projectId is available
  const fetchData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getAfterControlFormService(projectId);

      if (!res?.data) {
        throw new Error("No data received from server");
      }

      const filteredData = res.data.filter(
        (ele) => !ele?.isDeleted && ele?.status === "active"
      );

      const updatedFilteredData = filteredData.map((item) => {
        const updatedVisual = item.visual
          .filter((subItem) => subItem.inspection !== "na")
          .map((subItem) => ({
            ...subItem,
            inspection: null,
            documentFile: null,
          }));

        return {
          ...item,
          visual: updatedVisual,
          afterControlComment: "",
        };
      });

      setAfterControl(updatedFilteredData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message || "Failed to fetch data");
      toast.error("Error loading data: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Initialize component once
  useEffect(() => {
    if (projectId) {
      fetchData();
      setControlName(formatDateString());
    }
  }, [projectId, fetchData, formatDateString]);

  useEffect(() => {
    if (userId) {
      getProjectsDataByUserId(userId);
    }
  }, [userId]);

  // Computed properties
  const projectName = useMemo(() => {
    return afterControl[0]?.projectId?.projectName || "";
  }, [afterControl]);

  const counts = useMemo(() => {
    let activeCount = 0;
    let inactiveCount = 0;
    let dismissedCount = 0;

    afterControl.forEach((entry) => {
      switch (entry.status) {
        case "active":
          activeCount++;
          break;
        case "inactive":
          inactiveCount++;
          break;
        case "disassembled":
          dismissedCount++;
          break;
        default:
          break;
      }
    });

    return { activeCount, inactiveCount, dismissedCount };
  }, [afterControl]);

  // Handlers
  const handleCommentChange = (index, e) => {
    const updatedAfterControl = [...afterControl];
    updatedAfterControl[index] = {
      ...updatedAfterControl[index],
      afterControlComment: e.target.value,
    };
    setAfterControl(updatedAfterControl);
  };

  const validateForm = () => {
    if (!signatureImageone) {
      toast.error("Approval Signature is Required");
      return false;
    }

    // Add any additional validation as needed

    return true;
  };

  const getProjectsDataByUserId = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      const responseData = await getProjectByUserId(id);
      if (responseData) {
        setProjects(responseData?.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const sendDataToBackend = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const signature = {
        customer: { name: customerName, signature: signatureImage },
        approver: { name: approverName, signature: signatureImageone },
      };

      const payload = {
        projectName,
        projectId,
        userId: loggedInUser?._id,
        afterControl,
        signature,
        activeCount: counts.activeCount,
        inactiveCount: counts.inactiveCount,
        dismissedCount: counts.dismissedCount,
        controlName,
      };

      const response = await createAfterControlFormService(payload);

      if (response?.status === "Success") {
        toast.success("Created Successfully");
        navigate(`/admin/after-control`);
      } else {
        throw new Error(response?.message || "Failed to create form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  // Signature handlers
  const handleAddSignature = () => setModalOpen(true);
  const handleAddSignatureone = () => setModalOpenone(true);

  const handleSaveSignature = (signatureDataUrl, name) => {
    setSignatureImage(signatureDataUrl);
    setCustomerName(name);
    setModalOpen(false);
  };

  const handleSaveSignatureone = (signatureDataUrl, name) => {
    setSignatureImageone(signatureDataUrl);
    setApproverName(name);
    setModalOpenone(false);
  };

  const closeModal = () => setModalOpen(false);
  const closeModalone = () => setModalOpenone(false);

  // Image handlers
  const handelImage = (scaffold) => {
    if (!scaffold) return;
    setModalImageUrl(scaffold);
    setShowModal(true);
  };

  const handelImage1 = (index, visual) => {
    if (!visual || !visual.length) return;
    setCurrentIndex(index);
    setRecentViewVisual(visual);
    setShowModalVisual(true);
  };

  // Compute inspection stats
  const getTheNumberOfInspectionDetails = (data) => {
    if (!data || !Array.isArray(data)) {
      return { yes: 0, no: 0, na: 0, null: 0, totalInspection: 0 };
    }

    const counts = {
      yes: 0,
      no: 0,
      na: 0,
      null: 0,
      totalInspection: data.length,
    };

    data.forEach((item) => {
      if (item.inspection === "yes") {
        counts.yes += 1;
      } else if (item.inspection === "no") {
        counts.no += 1;
      } else if (item.inspection === "NA") {
        counts.na += 1;
      } else {
        counts.null += 1;
      }
    });

    return counts;
  };

  const handleConfirm = () => {
    if (projectId) {
      setIsProjectOpen(false);
    } else {
      toast.error("Please select a project.");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Render loading state
  if (loading && !afterControl.length) {
    return (
      <MainLayout title={"Create After Control Form"}>
        <div className="custom-container flex justify-center items-center py-20">
          <div className="text-center">
            <svg
              aria-hidden="true"
              className="inline w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <h1 className="font-bold mt-3">Loading...</h1>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Render error state
  if (error && !afterControl.length) {
    return (
      <MainLayout title={"Create After Control Form"}>
        <div className="custom-container flex justify-center items-center py-20">
          <div className="text-center text-red-600">
            <h1 className="font-bold text-xl mb-2">Error</h1>
            <p>{error}</p>
            <button
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
              onClick={fetchData}
            >
              Retry
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={"Create After Control Form"}>
      {/* Modal Components */}
      {showModal && (
        <Modal setShowModal={setShowModal} modalImageUrl={modalImageUrl} />
      )}

      {showModalVisual && (
        <ModalVisual
          setShowModalVisual={setShowModalVisual}
          visualData={recentViewVisual}
          afterControl={afterControl}
          setAfterControl={setAfterControl}
          currentIndex={currentIndex}
        />
      )}

      {/* Control Name */}
      <div className="custom-container flex flex-col mb-4">
        <label className="text-sm font-semibold">Control Name/Number:</label>
        <input
          className="!pl-2 !w-1/2 border !border-black !outline-none font-bold text-md"
          type="text"
          value={controlName}
          readOnly
        />
      </div>

      <div className="custom-container">
        {/* Project Header */}
        <table className="border-collapse w-full cursor-pointer">
          <thead className="border-b-2 border-b-[#CCCCCC]">
            <tr>
              <th className="sm:p-4 py-2 px-3 uppercase">{projectName}</th>
              <th className="sm:p-4 py-2 px-3 uppercase">
                {t("Activescaffolds")}
              </th>
            </tr>
          </thead>
        </table>

        {/* Scaffolds Table */}
        <div className="overflow-x-auto">
          <table className="border-collapse w-full">
            <thead className="border-b-2 border-b-[#CCCCCC]">
              <tr>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("ScaffoldName/type")}
                </th>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("ScaffoldID/Number")}
                </th>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("Specificlocation")}
                </th>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("Control")}
                </th>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("LastInspection")}
                </th>
                <th className="sm:p-4 py-2 px-3 sm:text-base text-sm text-left">
                  {t("AddComment")}
                </th>
              </tr>
            </thead>

            <tbody className="border-b border-b-[#CCCCCC]">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10">
                    <svg
                      aria-hidden="true"
                      className="inline w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <h1 className="font-bold mt-3">Loading...</h1>
                  </td>
                </tr>
              ) : afterControl?.length > 0 ? (
                afterControl.map((scaffold, index) => {
                  const scaffoldNames =
                    scaffold?.scaffoldName
                      ?.map((el) => `${el?.value}-${el?.key}`)
                      ?.join(", ") || "";

                  return (
                    <tr key={index} className="border-t border-t-[#CCCCCC]">
                      <td className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium">
                        {scaffoldNames}
                      </td>
                      <td className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium">
                        {scaffold?.scaffoldIdentificationNumber}
                      </td>
                      <td
                        className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium cursor-pointer"
                        onClick={() => handelImage(scaffold?.approvalFormImage)}
                      >
                        {scaffold?.location}
                      </td>
                      <td
                        onClick={() => setCurrentIndex(index)}
                        className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium"
                      >
                        <button
                          className={`button-text ${
                            getTheNumberOfInspectionDetails(scaffold?.visual)
                              .no > 0
                              ? "bg-red-400"
                              : getTheNumberOfInspectionDetails(
                                  scaffold?.visual
                                ).totalInspection ===
                                getTheNumberOfInspectionDetails(
                                  scaffold?.visual
                                ).yes +
                                  getTheNumberOfInspectionDetails(
                                    scaffold?.visual
                                  ).na
                              ? "bg-green-400"
                              : "bg-blue-400"
                          } text-white px-[10px] py-[5px] rounded-[5px] text-nowrap`}
                          onClick={() => handelImage1(index, scaffold?.visual)}
                          disabled={!scaffold?.visual?.length}
                        >
                          Visual Inspection
                        </button>
                      </td>
                      <td className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium">
                        {scaffold?.date
                          ? new Date(scaffold.date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="sm:p-4 py-2 px-3 sm:text-base text-sm font-medium">
                        <textarea
                          className="rounded border border-gray-300 p-3"
                          value={scaffold?.afterControlComment || ""}
                          onChange={(e) => handleCommentChange(index, e)}
                          rows={3}
                          cols={30}
                          placeholder={t("AddComment")}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Form Data Summary */}
        <div className="pb-[50px] mt-8">
          <div className="pb-[30px]">
            <p className="medium-title">{t("formDatas")}</p>
          </div>
          <div className="my-[30px]">
            <div className="flex flex-col lg:flex-row w-full">
              <div className="w-full lg:w-[20%]">
                <div className="flex justify-center items-center p-[20px]">
                  <div className="text-center">
                    <p className="medium-title pb-[20px] text-[#0072BB] h-[70px]">
                      {t("Active-scaffolds")}
                    </p>
                    <p className="medium-title text-[#626262]">
                      {counts.activeCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signatures Section */}
        <div className="flex flex-col gap-[30px] mt-[60px]">
          <div className="flex justify-between items-center">
            <p className="medium-title">{t("signature")}</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-[50px] justify-between items-center lg:pl-[100px]">
            {/* Customer Signature */}
            <div className="flex flex-col gap-[20px] w-full lg:w-[380px] items-center">
              {signatureImage ? (
                <>
                  <img
                    className="m-auto"
                    width={169}
                    src={signatureImage}
                    alt="Signature"
                  />
                  <p className="text-black text-[cap] font-Montserrat font-bold text-base leading-7">
                    {_.startCase(_.toLower(customerName))}
                  </p>
                </>
              ) : null}

              {signatureImage ? (
                <button
                  onClick={() => setSignatureImage(null)}
                  className="text-red-500"
                >
                  {t("clearSignature")}
                </button>
              ) : (
                <button onClick={handleAddSignature} className="sigbtn">
                  <img src="/addShape.svg" alt="sign-add" /> {t("AddSignature")}
                </button>
              )}

              <div className="w-full border"></div>
              <p>{t("customer'sSignature")}</p>
            </div>

            {/* Approver Signature */}
            <div className="flex flex-col gap-[20px] w-full lg:w-[380px] items-center">
              {signatureImageone ? (
                <>
                  <img
                    className="m-auto"
                    width={169}
                    src={signatureImageone}
                    alt="Signature"
                  />
                  <p className="text-black text-[cap] font-Montserrat font-bold text-base leading-7">
                    {_.startCase(_.toLower(approverName))}
                  </p>
                </>
              ) : null}

              {signatureImageone ? (
                <button
                  onClick={() => setSignatureImageone(null)}
                  className="text-red-500"
                >
                  {t("clearSignature")}
                </button>
              ) : (
                <button onClick={handleAddSignatureone} className="sigbtn">
                  <img src="/addShape.svg" alt="sign-add" />
                  {t("AddSignature")}
                </button>
              )}

              <div className="w-full border"></div>
              <div>
                {t("signatureOfTheApprover")}
                <span style={{ color: "red", fontSize: "1.5em" }}>*</span>
              </div>
            </div>
          </div>

          {/* Signature Modals */}
          <SignatureModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onSave={handleSaveSignature}
          />

          <SignatureModal
            isOpen={isModalOpenone}
            onClose={closeModalone}
            onSave={handleSaveSignatureone}
            inspector={approvalName}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-center gap-10 md:mt-[60px] sm:mt-8 mt-4 mb-[5px]">
          <button
            onClick={sendDataToBackend}
            disabled={loading}
            className={`button-text ${
              loading ? "bg-gray-400" : "bg-[#0072BB]"
            } text-white px-[20px] py-[10px] rounded-[5px] flex items-center`}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("Processing")}
              </>
            ) : (
              t("SubmitData")
            )}
          </button>
        </div>
      </div>

      {isProjectOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
              Select Project for New Approval Form
            </h2>

            {/* Search input */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search by project name or number..."
                className="w-full bg-gray-50 border border-gray-300 rounded-xl p-3 pl-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  onClick={() => setSearchTerm("")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>

            {projects.length === 0 ? (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">No Projects Available</span>
                </div>
                <p className="text-sm">
                  Please create a new project to continue.
                </p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">No Matching Projects</span>
                </div>
                <p className="text-sm">
                  No projects match your search. Try different keywords.
                </p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto mb-6 bg-gray-50 border border-gray-300 rounded-xl">
                {filteredProjects.map((project) => (
                  <div
                    key={project._id}
                    className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-blue-50 transition-colors duration-200 ${
                      projectId === project._id ? "bg-blue-100" : ""
                    }`}
                    onClick={() => setProjectId(project._id)}
                  >
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">
                          {project.projectName}
                        </div>
                        <div className="text-sm text-gray-600">
                          #{project.projectNumber}
                        </div>
                      </div>
                      {projectId === project._id && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-blue-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center space-x-2 my-6">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="text-xs font-medium text-gray-500 px-3 bg-white">
                OR
              </span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            <Link to="/admin/after-control" className="block mb-6">
              <button className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-200 font-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Create New Project
              </button>
            </Link>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <button
                onClick={handleGoBack}
                className="w-full bg-white border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                className={`w-full font-medium py-3 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !projectId && filteredProjects.length > 0
                    ? "bg-blue-400 text-white cursor-not-allowed opacity-70"
                    : "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg"
                }`}
                disabled={!projectId && filteredProjects.length > 0}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AfterControl;
